import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, Sparkles, Languages } from "lucide-react";
import Brand from "./Brand";
import { T, useI18n, setLang } from "../lib/i18n";

/** Compact EN/हिं toggle shared by staff consoles (and reusable elsewhere). */
export function LangToggle({ light = false }) {
  const { lang } = useI18n();
  const next = lang === "hi" ? "en" : "hi";
  return (
    <button
      onClick={() => setLang(next)}
      className="btn-ghost text-xs flex items-center gap-1.5"
      style={light ? { color: "rgba(255,255,255,0.85)" } : undefined}
      aria-label="Switch language"
      data-testid="lang-toggle"
    >
      <Languages className="w-3.5 h-3.5" strokeWidth={1.5} />
      {lang === "hi" ? "EN" : "हिं"}
    </button>
  );
}

export default function StaffShell({ title, subtitle, children, right = null }) {
  const { user, logout } = useAuth();
  useI18n(); // re-render the whole console instantly on language switch
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/vendor");
  };

  return (
    <div className="min-h-screen bg-motif">
      <header className="sticky top-0 z-30 glass border-b border-motif">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Brand small />
            <div className="hidden sm:flex items-center gap-2 ml-3 pl-4 border-l border-motif">
              <Sparkles className="w-3.5 h-3.5 text-primary-motif" strokeWidth={1.5} />
              <span className="text-xs tracking-luxe text-motif-muted" data-testid="role-badge">
                {user?.role || "staff"} {T("console")}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {right}
            <LangToggle />
            <button onClick={onLogout} className="btn-ghost text-xs flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              {T("Sign out")}
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-5 fadein">
          <h1 className="font-display text-3xl sm:text-4xl text-motif">{title}</h1>
          {subtitle && <p className="text-motif-muted text-sm mt-1">{subtitle}</p>}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}
